// Gestor de modales/ventanas HTML. Accesible (foco, Escape, aria).
export class ModalManager {
  constructor(root, bus) {
    this.root = root;
    this.bus = bus;
    this.overlay = document.createElement('div');
    this.overlay.className = 'modal-overlay hidden';
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.root.appendChild(this.overlay);
    this._stack = [];

    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay && this._current?.dismissible) this.close();
    });
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._current?.dismissible) this.close();
    });
    // Al girar / redimensionar, reajusta el escalado de los modales abiertos.
    window.addEventListener('resize', () => this._refitAll());
    window.addEventListener('orientationchange', () => this._refitAll());
  }

  /**
   * Escala el modal si NO cabe en la pantalla, para que NUNCA tenga scroll ni desborde.
   * Mide el alto natural (sin límite) y aplica transform:scale si hace falta.
   */
  _fit(modal) {
    if (!modal) return;
    // El MARCO del modal mantiene su tamaño (90vw × 90vh). Es el CONTENIDO
    // (.modal-scale) el que se reduce con transform:scale si no cabe, para que
    // NUNCA haya scroll y SIN deformar (escala uniforme, conserva proporciones).
    const body = modal.querySelector('.modal-body');
    const scale = modal.querySelector('.modal-scale');
    if (!body || !scale) return;
    scale.style.transform = 'none';
    const availW = body.clientWidth;
    const availH = body.clientHeight;
    const natW = scale.scrollWidth;
    const natH = scale.scrollHeight;
    const s = Math.min(1, availW / natW || 1, availH / natH || 1);
    // Amigos/Sala NO se escalan: llenan el ancho (columnas a 7% por cada lado) y la
    // lista tiene scroll interno. El resto de ventanas sí se reducen para caber.
    const fill = modal.classList.contains('friends-modal') || modal.classList.contains('lobby-modal') || modal.classList.contains('help-modal') || modal.classList.contains('settings-modal') || modal.classList.contains('results-premium') || modal.classList.contains('ach-modal') || modal.classList.contains('def-modal') || modal.classList.contains('rank-modal');
    if (!fill) {
      scale.style.transformOrigin = 'center center';
      scale.style.transform = s < 1 ? `scale(${s})` : '';
    }
    // Re-ajusta ante CUALQUIER cambio de tamaño del contenido (QR async, roster, etc.).
    if (!modal._fitRO && typeof ResizeObserver !== 'undefined') {
      modal._fitRO = new ResizeObserver(() => this._fit(modal));
      modal._fitRO.observe(scale);
      modal._fitRO.observe(body);
    }
  }

  _refitAll() {
    for (const s of this._stack) this._fit(s.modal);
  }

  get _current() {
    return this._stack[this._stack.length - 1] || null;
  }

  /**
   * Abre un modal.
   * @param {object} opts { title, bodyHTML, buttons:[{label,className,onClick,close}], dismissible, className, onMount }
   */
  open(opts) {
    const modal = document.createElement('div');
    modal.className = `modal ${opts.className || ''}`;

    const html = `
      ${opts.title ? `<h2 class="modal-title">${opts.title}</h2>` : ''}
      <div class="modal-body"><div class="modal-scale">${opts.bodyHTML || ''}</div></div>
      ${opts.buttons?.length ? '<div class="modal-actions"></div>' : ''}
    `;
    modal.innerHTML = html;

    if (opts.buttons?.length) {
      const actions = modal.querySelector('.modal-actions');
      for (const b of opts.buttons) {
        const btn = document.createElement('button');
        btn.className = `btn ${b.className || ''}`;
        btn.textContent = b.label;
        btn.addEventListener('click', () => {
          this.bus.emit('audio:sfx', { name: 'button' });
          const keep = b.onClick?.();
          if (b.close !== false && keep !== true) this.close();
        });
        actions.appendChild(btn);
      }
    }

    // Solo se muestra el modal superior: oculta el anterior para que no se
    // solapen dos ventanas de 90vw (p. ej. Pausa → Ajustes).
    if (this._current) this._current.modal.style.display = 'none';
    this.overlay.appendChild(modal);
    this._stack.push({ modal, dismissible: opts.dismissible !== false });
    this.overlay.classList.remove('hidden');
    opts.onMount?.(modal);
    this._fit(modal); // escala si no cabe (sin scroll)
    // Re-ajusta varias veces por si el contenido carga tarde (QR, imágenes, roster).
    requestAnimationFrame(() => this._fit(modal));
    setTimeout(() => this._fit(modal), 250);
    setTimeout(() => this._fit(modal), 700);
    // Enfoca el CONTENEDOR del modal, NO un campo de texto: enfocar un input abriría
    // el teclado del móvil automáticamente (p. ej. al abrir el perfil). Las ventanas que
    // sí quieren teclado (unirse a sala) enfocan su input explícitamente.
    modal.setAttribute('tabindex', '-1');
    modal.focus({ preventScroll: true });
    return modal;
  }

  close() {
    const top = this._stack.pop();
    if (top) { top.modal._fitRO?.disconnect(); top.modal.remove(); }
    if (this._stack.length === 0) {
      this.overlay.classList.add('hidden');
    } else {
      // Vuelve a mostrar (y reajusta) el modal que queda debajo.
      const now = this._current;
      if (now) { now.modal.style.display = ''; this._fit(now.modal); }
    }
  }

  closeAll() {
    for (const s of this._stack) { s.modal._fitRO?.disconnect(); s.modal.remove(); }
    this._stack = [];
    this.overlay.classList.add('hidden');
  }

  /** Notificación temporal (toast). `center`: centrado y semitransparente. `dim`: abajo pero
   *  semitransparente (p. ej. avisos de logro). */
  toast(message, { icon = '', duration = 2200, center = false, dim = false } = {}) {
    const t = document.createElement('div');
    t.className = 'toast' + (center ? ' toast-center' : '') + (dim ? ' toast-dim' : '');
    t.innerHTML = `${icon ? `<span class="toast-icon">${icon}</span>` : ''}<span>${message}</span>`;
    this.root.appendChild(t);
    requestAnimationFrame(() => t.classList.add('show'));
    setTimeout(() => {
      t.classList.remove('show');
      setTimeout(() => t.remove(), 300);
    }, duration);
  }
}
